import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faDownload, faUndo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { downloadCsv } from '../reports/shared/reportUtils';
const { rootUrl } = require('../../../config/config');

const ArchivedCars = () => {
    const { user, token } = useAuth();
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Restore modal state
    const [restoreModalOpen, setRestoreModalOpen] = useState(false);
    const [restorePlate, setRestorePlate] = useState('');
    const [restoreReason, setRestoreReason] = useState('');
    const [restoreLoading, setRestoreLoading] = useState(false);
    const [restoreError, setRestoreError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const handleExportArchivedCars = useCallback(() => {
        if (!cars.length) return;

        const headers = [
            'Plate No.',
            'Make',
            'Model',
            'Year',
            'Archived By',
            'Archived Date',
            'Reason'
        ];

        const rows = cars.map((car) => [
            car.plate_no || '',
            car.make || '',
            car.model || '',
            car.year || '',
            car.archived_by || '',
            car.archived_at ? formatDateTime(car.archived_at) : '',
            car.archive_reason || ''
        ]);

        const filename = `archived_cars_${new Date().toISOString().slice(0, 10)}.csv`;
        downloadCsv(headers, rows, filename);
    }, [cars]);

    const fetchArchivedCars = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${rootUrl}/cars/archived`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCars(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load archived vehicles.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArchivedCars();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openRestoreModal = (plate_no) => {
        setRestorePlate(plate_no);
        setRestoreReason('');
        setRestoreError('');
        setRestoreModalOpen(true);
    };

    const confirmRestore = async () => {
        if (!restoreReason.trim()) {
            setRestoreError('Restore reason is required.');
            return;
        }
        setRestoreLoading(true);
        setRestoreError('');
        try {
            await axios.post(
                `${rootUrl}/cars/${restorePlate}/restore`,
                {
                    restore_reason: restoreReason,
                    restored_by: user?.username || user?.email || 'system'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setRestoreModalOpen(false);
            setSuccessMsg(`Vehicle ${restorePlate} restored successfully.`);
            setTimeout(() => setSuccessMsg(''), 4000);
            fetchArchivedCars();
        } catch (err) {
            setRestoreError(err.response?.data?.message || 'Failed to restore vehicle.');
        } finally {
            setRestoreLoading(false);
        }
    };

    const formatDate = (val) => {
        if (!val) return '—';
        return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatDateTime = (val) => {
        if (!val) return '—';
        return new Date(val).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
                    <div className="text-center lg:text-left">
                        <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Archived Vehicles</h1>
                        <div className="w-24 h-1 bg-[#FF5733] mx-auto lg:mx-0 rounded-full mb-4"></div>
                        <p className="text-lg text-[#2a2e30] opacity-80">
                            Vehicles removed from the active fleet
                        </p>
                    </div>
                    <div className="flex justify-center lg:justify-end">
                        <button
                            type="button"
                            onClick={handleExportArchivedCars}
                            disabled={!cars.length}
                            className="inline-flex items-center gap-2 rounded-sm border border-[#FF5733] px-4 py-2 text-sm font-semibold text-[#FF5733] hover:bg-[#fff4ef] disabled:cursor-not-allowed disabled:opacity-50"
                            title="Export archived vehicles to CSV"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            Export CSV
                        </button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {successMsg && (
                    <div className="mb-4 px-4 py-3 rounded-lg bg-green-100 border border-green-300 text-green-800 text-sm font-medium">
                        {successMsg}
                    </div>
                )}

                {error && (
                    <div className="mb-4 px-4 py-3 rounded-lg bg-red-100 border border-red-300 text-red-800 text-sm font-medium">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-[#FF5733]" />
                            <span className="ml-3 text-gray-500">Loading archived vehicles...</span>
                        </div>
                    ) : cars.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <FontAwesomeIcon icon={faArchive} className="text-5xl mb-4 opacity-30" />
                            <p className="text-lg">No archived vehicles found.</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full table-auto text-left">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Plate No.</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Make / Model</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Year</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Archived By</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Archived Date</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Reason</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cars.map((car, idx) => (
                                            <tr key={car.plate_no} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                                <td className="px-4 py-3 font-semibold text-[#2a2e30] text-sm">{car.plate_no}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{[car.make, car.model].filter(Boolean).join(' / ') || '—'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{car.year || '—'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{car.archived_by || '—'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{formatDateTime(car.archived_at)}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={car.archive_reason}>{car.archive_reason || '—'}</td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => openRestoreModal(car.plate_no)}
                                                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-sm px-3 py-1 flex items-center gap-1"
                                                    >
                                                        <FontAwesomeIcon icon={faUndo} />
                                                        Restore
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="sm:hidden divide-y divide-gray-100">
                                {cars.map((car) => (
                                    <div key={car.plate_no} className="p-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <span className="font-bold text-[#2a2e30] text-base">{car.plate_no}</span>
                                                <p className="text-sm text-gray-500">{[car.make, car.model].filter(Boolean).join(' / ') || '—'} {car.year ? `(${car.year})` : ''}</p>
                                            </div>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-300">
                                                <FontAwesomeIcon icon={faArchive} className="mr-1" /> Archived
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500 space-y-1 mb-3">
                                            <p><span className="font-medium">Archived by:</span> {car.archived_by || '—'}</p>
                                            <p><span className="font-medium">Date:</span> {formatDate(car.archived_at)}</p>
                                            {car.archive_reason && (
                                                <p><span className="font-medium">Reason:</span> {car.archive_reason}</p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => openRestoreModal(car.plate_no)}
                                            className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-sm px-3 py-1 flex items-center gap-1"
                                        >
                                            <FontAwesomeIcon icon={faUndo} />
                                            Restore Vehicle
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <p className="text-center text-sm text-gray-400 mt-4">
                    {cars.length > 0 && `${cars.length} archived vehicle${cars.length !== 1 ? 's' : ''}`}
                </p>
            </div>

            {/* Restore Modal */}
            {restoreModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-bold text-[#2a2e30] mb-1">Restore Vehicle</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Restoring <strong>{restorePlate}</strong> will return it to the active fleet and allow new transactions and driver assignments.
                        </p>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Reason for restoring <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] resize-none"
                            rows={3}
                            placeholder="e.g. Returned to service, error correction..."
                            value={restoreReason}
                            onChange={(e) => setRestoreReason(e.target.value)}
                        />
                        {restoreError && (
                            <p className="text-red-500 text-sm mt-1">{restoreError}</p>
                        )}
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setRestoreModalOpen(false)}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2"
                                disabled={restoreLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRestore}
                                className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={restoreLoading}
                            >
                                {restoreLoading ? 'Restoring...' : 'Restore Vehicle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArchivedCars;
